// @ts-check
/**
 * Channel Hub — the router of an origin's Pub/Sub.
 *
 * Runs in a `SharedWorker` where available, else a `Worker` — the same module
 * either way; only the number of endpoints differs. The topic grammar and the
 * merge a burst coalesces by are `topics.js`'s; the other end of the wire is
 * `ChannelEndpoint` (`channel-endpoint.js`).
 *
 * **Addressed, not broadcast.** Every endpoint declares the topics it listens
 * to, and a publication reaches the endpoints whose patterns match it — never
 * the publisher itself.
 *
 * **Coalescing lives here.** Publications on one topic within the window
 * collapse to one, merged by `mergePublications` unless the hub is given
 * another merge. A publication merged from different origins keeps no origin,
 * and reaches both.
 *
 * Wire messages between endpoint and hub — internal, listed so a reader of
 * either side can follow a transcript:
 *
 * | Direction | `type` | Carries |
 * | --- | --- | --- |
 * | endpoint → hub | `channels:connect` | `name`, `topics` |
 * | endpoint → hub | `channels:topics` | `topics` — the endpoint's whole current list |
 * | endpoint → hub | `channels:publish` | `publication` `{ topic, data, origin, at }`, transferables |
 * | endpoint → hub | `channels:attach` | a transferred `MessagePort` for another endpoint, and its `name` |
 * | endpoint → hub | `channels:detach` | `name` — closes the endpoints this one attached under it |
 * | endpoint → hub | `channels:close` | — |
 * | hub → endpoint | `channels:connected` | `name`, `shared`, `endpoints` |
 * | hub → endpoint | `channels:publications` | `publications`, `notice` |
 *
 * Carried from the ConRes 2026 tree's `classes/channels/channel-hub.js` with
 * one change: what the hub says goes through a `report` option, warnings and
 * errors to the console by default and connection notices nowhere, rather
 * than through `console.log` unconditionally.
 *
 * @module @smotaal/platform-orchestration/lib/channels/channel-hub
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1 (code generation)
 */

import {
    DEFAULT_COALESCE_MILLISECONDS, matchesTopic, mergePublications, normaliseTopics,
} from './topics.js';

/** @typedef {import('../ports/port.js').PortLike} HubPort */
/** @typedef {import('./topics.js').Publication<any>} HubPublication */

/**
 * @typedef {{
 *   port: HubPort,
 *   name: string | null,
 *   topics: string[],
 *   parent: EndpointRecord | null,
 *   children: Set<EndpointRecord>,
 * }} EndpointRecord
 */

/** @typedef {(level: 'log' | 'warn' | 'error', message: string, detail?: unknown) => void} Reporter */

/**
 * The default reporter: warnings and errors to the console, notices nowhere.
 *
 * @type {Reporter}
 */
export function defaultReporter(level, message, detail) {
    if (level === 'log') return;
    if (detail === undefined) console[level](message);
    else console[level](message, detail);
}

/**
 * The router.
 *
 * ```js
 * // the hub's entrypoint
 * ChannelHub.serve(globalThis);
 * ```
 */
export class ChannelHub {
    /** @type {Set<EndpointRecord>} */
    #endpoints = new Set();

    /** @type {Map<string, { publication: HubPublication, origin: EndpointRecord | null }>} */
    #pending = new Map();

    /** @type {ReturnType<typeof setTimeout> | null} */
    #flushTimer = null;

    /** @type {(publication: HubPublication) => string} */
    #key = publication => publication.topic;

    /** @type {(previous: HubPublication, next: HubPublication) => HubPublication} */
    #merge = mergePublications;

    #windowMs = DEFAULT_COALESCE_MILLISECONDS;

    #shared;

    #label;

    /** @type {Reporter} */
    #report;

    /**
     * Serves a worker scope.
     *
     * @param {any} scope `SharedWorkerGlobalScope` or `DedicatedWorkerGlobalScope`
     *   — or, in a test, any object with an `onconnect` slot (shared) or a
     *   `postMessage` (dedicated).
     * @param {{ shared?: boolean, label?: string, windowMs?: number, report?: Reporter }} [options]
     *   `shared` is detected from the scope when omitted.
     */
    static serve(scope, options = {}) {
        return new ChannelHub(scope, options);
    }

    /**
     * @param {any} scope
     * @param {{ shared?: boolean, label?: string, windowMs?: number, report?: Reporter }} [options]
     */
    constructor(scope, { shared, label = '[ChannelHub]', windowMs = DEFAULT_COALESCE_MILLISECONDS, report = defaultReporter } = {}) {
        this.#shared = shared ?? (typeof SharedWorkerGlobalScope !== 'undefined'
            && scope instanceof SharedWorkerGlobalScope);
        this.#label = label;
        this.#windowMs = windowMs;
        this.#report = report;

        if (this.#shared) {
            scope.onconnect = (/** @type {MessageEvent} */ event) => {
                for (const port of event.ports) this.#connect(port, null);
            };
        } else {
            // A dedicated worker has exactly one implicit port: its own scope.
            this.#connect(scope, null);
            scope.postMessage({ type: 'channels:ready', shared: false });
        }
    }

    /** Whether this hub is shared between tabs. */
    get shared() { return this.#shared; }

    /** How many endpoints are connected. */
    get endpoints() { return this.#endpoints.size; }

    /** Every connected endpoint's name and topics — for tests and diagnostics. */
    get registry() {
        return [...this.#endpoints].map(record => ({ name: record.name, topics: [...record.topics] }));
    }

    /**
     * How repeats collapse: publications with the same `key` within `windowMs`
     * become one, combined by `merge`. Defaults: by topic, arrays unioned.
     *
     * @param {{
     *   key?: (publication: HubPublication) => string,
     *   merge?: (previous: HubPublication, next: HubPublication) => HubPublication,
     *   windowMs?: number,
     * }} options
     */
    coalesce({ key, merge, windowMs } = {}) {
        if (key) this.#key = key;
        if (merge) this.#merge = merge;
        if (windowMs !== undefined) this.#windowMs = windowMs;
        return this;
    }

    /**
     * @param {HubPort} port
     * @param {EndpointRecord | null} parent
     */
    #connect(port, parent) {
        /** @type {EndpointRecord} */
        const record = { port, name: null, topics: [], parent, children: new Set() };
        this.#endpoints.add(record);
        parent?.children.add(record);

        port.onmessage = (/** @type {any} */ event) => this.#receive(record, event);
        if ('onmessageerror' in port) {
            port.onmessageerror = (/** @type {any} */ event) =>
                this.#report('error', `${this.#label} A message from ${record.name ?? 'an endpoint'} could not be deserialized`, event);
        }
        if (typeof port.start === 'function') port.start();
        return record;
    }

    /**
     * @param {EndpointRecord} record
     * @param {{ data: any, ports?: readonly MessagePort[] }} event
     */
    #receive(record, event) {
        const data = event.data ?? {};
        switch (data.type) {
            case 'channels:connect': {
                // An attaching page may have named this endpoint already.
                record.name ??= `${data.name ?? 'unnamed'}`;
                record.topics = normaliseTopics(data.topics);
                this.#send(record, {
                    type: 'channels:connected', name: record.name, shared: this.#shared, endpoints: this.#endpoints.size,
                });
                this.#report('log', `${this.#label} Endpoint connected — ${record.name} [${record.topics.join(', ')}] (${this.#endpoints.size} connected)`);
                return;
            }
            case 'channels:topics':
                record.topics = normaliseTopics(data.topics);
                return;
            case 'channels:publish':
                if (data.publication?.topic) this.#announce(data.publication, record);
                return;
            case 'channels:attach': {
                const port = event.ports?.[0];
                if (!port) {
                    this.#report('error', `${this.#label} ${record.name ?? 'An endpoint'} attached nothing — the message carried no port`);
                    return;
                }
                const child = this.#connect(port, record);
                if (data.name) child.name = `${data.name}`;
                return;
            }
            case 'channels:detach':
                for (const child of [...record.children]) {
                    if (child.name === `${data.name}`) this.#drop(child, 'detached by its page');
                }
                return;
            case 'channels:close':
                this.#drop(record, 'closed');
                return;
            default:
                this.#report('warn', `${this.#label} Ignored an unrecognized message from ${record.name ?? 'an endpoint'}`, data);
        }
    }

    /**
     * @param {EndpointRecord} record
     * @param {string} why
     */
    #drop(record, why) {
        if (!this.#endpoints.has(record)) return;
        this.#endpoints.delete(record);
        record.parent?.children.delete(record);
        for (const child of [...record.children]) this.#drop(child, `its parent ${why}`);
        try {
            record.port.close?.();
        } catch (error) {
            this.#report('warn', `${this.#label} The ${record.name ?? 'endpoint'} port would not close`, error);
        }
        this.#report('log', `${this.#label} Endpoint left — ${record.name ?? 'unnamed'}, ${why} (${this.#endpoints.size} connected)`);
    }

    /**
     * @param {HubPublication} publication
     * @param {EndpointRecord} origin
     */
    #announce(publication, origin) {
        const key = this.#key(publication);
        const previous = this.#pending.get(key);
        const merged = previous ? this.#merge(previous.publication, publication) : publication;
        // Coalescing across DIFFERENT origins would let a publication escape
        // the endpoint that needs to hear it, so a collapsed pair keeps none.
        const collapsedOrigin = previous && previous.origin !== origin ? null : origin;
        this.#pending.set(key, { publication: merged, origin: collapsedOrigin });

        if (this.#flushTimer) return;
        this.#flushTimer = setTimeout(() => this.flush(), this.#windowMs);
    }

    /** Delivers everything pending now. Called by the window timer; callable by a test. */
    flush() {
        if (this.#flushTimer) clearTimeout(this.#flushTimer);
        this.#flushTimer = null;
        const entries = [...this.#pending.values()];
        this.#pending.clear();
        if (!entries.length) return;

        let delivered = 0;
        for (const record of [...this.#endpoints]) {
            const publications = entries
                .filter(entry => entry.origin !== record)
                .filter(entry => record.topics.some(pattern => matchesTopic(pattern, entry.publication.topic)))
                .map(entry => entry.publication);
            if (!publications.length) continue;
            delivered++;
            const notice =
                `Fanning out ${publications.length} publication(s) ` +
                `[${publications.map(publication => publication.topic).join(', ')}] to ${record.name ?? 'an endpoint'}`;
            // A SharedWorker's console is not the page's — it is reachable only
            // through `chrome://inspect` — so the notice rides WITH the
            // publications and the page logs it where the operator is looking.
            this.#send(record, { type: 'channels:publications', publications, notice });
        }
        this.#report('log',
            `${this.#label} Flushed ${entries.length} publication(s) ` +
            `[${entries.map(entry => entry.publication.topic).join(', ')}] to ${delivered} of ${this.#endpoints.size} endpoint(s)`);
    }

    /**
     * @param {EndpointRecord} record
     * @param {Record<string, any>} message
     */
    #send(record, message) {
        try {
            record.port.postMessage(message);
        } catch (error) {
            // A tab or worker that went away takes its port with it. Dropping
            // it is correct; doing so without a word is not.
            this.#report('warn', `${this.#label} Dropping ${record.name ?? 'an endpoint'} whose port would not accept a message`, error);
            this.#drop(record, 'its port refused a message');
        }
    }
}
